import { Badge, Icon, ProviderLogo, Spinner, ThemeColor, Tooltip } from "@deligoo/ui"
import clsx from "clsx"
import dayjs from "dayjs"
import {
  ComponentProps,
  CSSProperties,
  Dispatch,
  memo,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import { shallowEqual } from "react-redux"
import { AutoSizer } from "react-virtualized/dist/commonjs/AutoSizer"
import { areEqual, FixedSizeList } from "react-window"

import { useAppSelector } from "@/App"
import { Clipboard } from "@/components/Clipboard"
import { ContextMenu, openContextMenu } from "@/components/ContextMenu"
import { Table, ThProps } from "@/components/Table"
import { OrderItemContext, useOrdersContextMenuTrigger } from "@/modules/OrderContextMenu"
import { selectClientById } from "@/store/clients"
import { selectOrderById, selectOrdersStatus, selectSortedFilteredOrdersWithDriversIds } from "@/store/orders"
import { Order, OrderPointState, OrderState } from "@/types"
import { ClientUtils, createGenericContext, formatDelay, formatTime, getIconFor, OrderUtils } from "@/utils"

import cls from "../OrdersListView.module.scss"
import { sortOrderListColumn } from "../sortOrderListColumn"

const TABLE_HEADERS = ["ordinal", "provider", "order_id", "pickup", "delivery", "params", "driver", "state"] as const

export type OrdersTableHeader = typeof TABLE_HEADERS[number]

type OrdersTableContextProps = {
  featuredClient: string | null
  setFeaturedClient: Dispatch<SetStateAction<string | null>>
  featuredDriver: string | null
  setFeaturedDriver: Dispatch<SetStateAction<string | null>>
}

const [useOrdersTableContext, OrdersTableContextProvider] = createGenericContext<OrdersTableContextProps>()

type OrdersTableProps = {
  filters: Array<(order: Order) => boolean>
  virtualized?: boolean
}

export const OrdersTable = ({ filters, virtualized }: OrdersTableProps) => {
  const [sorter, setSorter] = useState<((a: Order, b: Order) => number) | null>(null)

  const ordersIds = useAppSelector(selectSortedFilteredOrdersWithDriversIds(sorter, ...filters), shallowEqual)
  const ordersStatus = useAppSelector(selectOrdersStatus)

  const ordersIdsByNewest = useMemo(() => [...ordersIds].reverse(), [ordersIds])

  const [currentColumnSort, setCurrentColumnSort] = useState<OrdersTableHeader | null>("state")
  const [isSortedDesc, setIsSortedDesc] = useState(false)

  const [featuredClient, setFeaturedClient] = useState<string | null>(null)
  const [featuredDriver, setFeaturedDriver] = useState<string | null>(null)

  const { t } = useTranslation()

  function onColumnSort(column: OrdersTableHeader) {
    const currentIsSortedDesc = column === currentColumnSort ? !isSortedDesc : false
    const columnSortCallback = sortOrderListColumn(column, currentIsSortedDesc)

    if (columnSortCallback) setSorter(columnSortCallback)
    setIsSortedDesc(currentIsSortedDesc)
    setCurrentColumnSort(column)
  }

  function getThSortedBy(column: OrdersTableHeader): ThProps["sortedBy"] {
    if (currentColumnSort === column) {
      return isSortedDesc ? "desc" : "asc"
    }
  }

  useEffect(() => {
    if (currentColumnSort) onColumnSort(currentColumnSort)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <OrdersTableContextProvider value={{ featuredClient, setFeaturedClient, featuredDriver, setFeaturedDriver }}>
      <Table
        minWidth="1750px"
        columns="90px minmax(100px, 210px) 160px minmax(360px, 1fr) minmax(360px, 1fr) 200px minmax(150px, 220px) 220px minmax(90px, 1fr)"
        loading={!ordersStatus || ordersStatus === "loading"}
        error={ordersStatus === "error"}
        empty={ordersIdsByNewest.length === 0}
        virtualized={virtualized}
      >
        <Table.Thead>
          <Table.Tr>
            {TABLE_HEADERS.map((header) => (
              <Table.Th sortedBy={getThSortedBy(header)} onClick={() => onColumnSort(header)} key={header}>
                {t(`orders_list_headers.${header}`)}
              </Table.Th>
            ))}
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {virtualized ? (
            <AutoSizer>
              {({ height, width }) => (
                <FixedSizeList
                  height={height}
                  width={width}
                  overscanCount={4}
                  itemData={ordersIdsByNewest}
                  itemCount={ordersIdsByNewest.length}
                  itemKey={(index, data) => data[index]}
                  itemSize={106}
                >
                  {OrderListRow}
                </FixedSizeList>
              )}
            </AutoSizer>
          ) : (
            ordersIdsByNewest.map((orderId, index) => <OrderListRow data={ordersIdsByNewest} index={index} />)
          )}
        </Table.Tbody>
      </Table>
    </OrdersTableContextProvider>
  )
}

type OrderListRowProps = {
  data: Array<Order["id"]>
  index: number
  style?: CSSProperties
}

const OrderListRow = memo(({ data, index, style }: OrderListRowProps) => {
  const orderId = data[index]

  return <OrderListItem orderId={orderId} index={index} style={style} key={orderId} />
}, areEqual)

const OrderListItem = ({ orderId, index, style }: { orderId: Order["id"]; index: number; style?: CSSProperties }) => {
  const { setOrderContextMenuAction } = useContext(OrderItemContext) || {}

  const { featuredClient, setFeaturedClient, featuredDriver, setFeaturedDriver } = useOrdersTableContext()

  const order = useAppSelector(selectOrderById(orderId), shallowEqual)
  const client = useAppSelector(order?.client_id ? selectClientById(order.client_id) : () => undefined, shallowEqual)

  const contextMenuRef = useOrdersContextMenuTrigger({ orderId })

  const { t } = useTranslation()

  if (!order || !setOrderContextMenuAction) return null

  const pickupDelay = OrderUtils.getPointDelay(order.pickup!)
  const deliveryDelay = OrderUtils.getPointDelay(order.delivery!)

  return (
    <Table.Tr
      onClick={() => setOrderContextMenuAction({ orderId, action: "show_details_panel" })}
      style={style}
      ref={contextMenuRef}
    >
      <Table.Td>
        <span className="text-desc text-muted">{index + 1}</span>
      </Table.Td>

      <Table.Td>
        {order.provider !== "deligoo" ? (
          <ProviderLogo provider={order.provider} marketplace={order.marketplace_kind} className={cls.provider} />
        ) : (
          "-"
        )}
      </Table.Td>

      <Table.Td>
        <Clipboard className="h100 text-primary">{order.uid}</Clipboard>
        <div className="text-desc text-muted mt-1">{dayjs(order.created_at).format("HH:mm")}</div>

        {client && (
          <div className="text-desc text-muted mt-1">
            <Icon name={ClientUtils.getIconForLocationType(client.location_type)} size="small" gutter="right" />
            {t(`clients:location_types.${client.location_type}`)}
          </div>
        )}
      </Table.Td>

      <Table.Td className={clsx(featuredClient === order.pickup?.name && cls.featuredOrder)}>
        {order.pickup ? (
          <>
            <div
              className="h100 truncate mb-2 d-block"
              onClick={(e) => {
                setFeaturedClient((currentValue) => (currentValue !== order.pickup!.name ? order.pickup!.name : null))
                e.stopPropagation()
              }}
            >
              {order.pickup.name}
            </div>

            <div className="text-desc truncate mb-2" title={order.pickup.address}>
              {order.pickup.address}
            </div>

            <div className={cls.orderStatus}>
              <div className={cls.section}>
                {order.pickup.estimated_time ? (
                  <>
                    {formatTime(order.pickup.estimated_time)}
                    <Icon name="short-arrow-right" size="tiny" color="muted" className="mx-2" />
                    {formatTime(OrderUtils.getPointEstimatedTimeWithDuration(order.pickup))}
                  </>
                ) : (
                  <Spinner size="small" inline />
                )}

                {OrderUtils.shouldShowDelay(order.pickup, pickupDelay) && (
                  <span className={`ml-2 text-${OrderUtils.getPointDelayColor(pickupDelay)}`}>
                    {formatDelay(pickupDelay!, " (", ")")}
                  </span>
                )}
              </div>

              <div className={clsx(cls.section, cls.status, `status-${order.pickup.state}`)}>
                <Icon name={getIconFor(order.pickup.state)} size="small" gutter="right" />
                {t(`orders:${order.pickup.state}`)}
              </div>
            </div>
          </>
        ) : (
          "–"
        )}
      </Table.Td>

      <Table.Td>
        {order.delivery ? (
          <>
            <div className="h100 mb-2">{order.delivery.phone}</div>

            <div className="text-desc truncate mb-2" title={order.delivery.address}>
              {order.delivery.address}
            </div>

            <div className={cls.orderStatus}>
              <div className={cls.section}>
                {order.delivery.estimated_time ? (
                  <>
                    {formatTime(order.delivery.estimated_time)}
                    <Icon name="short-arrow-right" size="tiny" color="muted" className="mx-2" />
                    {formatTime(OrderUtils.getPointEstimatedTimeWithDuration(order.delivery))}
                  </>
                ) : (
                  <Spinner size="small" inline />
                )}

                {OrderUtils.shouldShowDelay(order.delivery, deliveryDelay) && (
                  <span className={`ml-2 text-${OrderUtils.getPointDelayColor(deliveryDelay)}`}>
                    {formatDelay(deliveryDelay!, " (", ")")}
                  </span>
                )}
              </div>

              <div className={clsx(cls.section, cls.status, `status-${order.delivery.state}`)}>
                <Icon name={getIconFor(order.delivery.state)} size="small" gutter="right" />
                {t(`orders:${order.delivery.state}`)}
              </div>
            </div>
          </>
        ) : (
          "–"
        )}
      </Table.Td>

      <Table.Td>
        {order.pickup_in && order.pickup_at && (
          <div className="text-desc">
            Czas: {order.pickup_in} min ({dayjs(order.pickup_at).format("HH:mm")})
          </div>
        )}

        <div className="text-desc my-1">
          Przypisanie: {order.perform_at ? dayjs(order.perform_at).format("HH:mm") : "–"}
        </div>

        <div className="text-desc">
          Płatność:{" "}
          {order.payment_form === "paid" && order.payment_status !== "completed" ? (
            <>
              {t(`orders:payment_status.${order.payment_status || "in_progress"}`)}
              <PaymentStatusIcon paymentStatus={order.payment_status || "in_progress"} size="small" className="ml-1" />
            </>
          ) : (
            <span className="text-success">
              {t(`orders:payment_form.${order.payment_form}`)}
              <Icon name="check-circle" size="small" className="ml-1" />
            </span>
          )}
        </div>
      </Table.Td>

      <Table.Td className={clsx(featuredDriver === order.driver?.name && cls.featuredDriver)}>
        <div
          className="h100"
          onClick={(e) => {
            if (order.driver?.name) {
              setFeaturedDriver((currentValue) => (currentValue !== order.driver!.name ? order.driver!.name : null))
            }
            e.stopPropagation()
          }}
        >
          {order.driver?.name && (order.reassigned || order.force_assignment) ? (
            <Tooltip content={t(`drivers:${order.force_assignment ? "force_assignment" : "reassigned"}`)}>
              <div className="d-flex align-center">
                {order.driver.name}
                <Icon name="edit" color="warning" gutter="left" />
              </div>
            </Tooltip>
          ) : (
            order.driver?.name || "–"
          )}
        </div>
      </Table.Td>

      <Table.Td>
        <Badge
          size="small"
          color={getStateColor(order.state)}
          variation={getStateBadgeVariation(order.state)}
          iconProps={{
            name: getIconFor(order.state),
          }}
        >
          {t(`orders:${order.state}`)}
        </Badge>
      </Table.Td>

      <Table.Td className="text-right">
        <ContextMenu.Button onClick={openContextMenu} />
      </Table.Td>
    </Table.Tr>
  )
}

type PaymentStatusIconProps = {
  paymentStatus: Order["payment_status"]
} & Omit<ComponentProps<typeof Icon>, "name" | "color">

const PaymentStatusIcon = ({ paymentStatus, ...props }: PaymentStatusIconProps) => {
  switch (paymentStatus) {
    case "chargeback":
      return <Icon name="reply" color="primary" {...props} />
    case "failed":
      return <Icon name="close-circle" color="alert" {...props} />
    case "completed":
      return <Icon name="check-circle" color="success" {...props} />
    // case "in_progress":
    default:
      return <Icon name="status" color="muted" {...props} />
    // return <Spinner size="small" className={props.className} />
  }
}

function getStateColor(state: OrderState | OrderPointState): ThemeColor {
  switch (state) {
    case "assigned":
    case "published":
      return "warning"
    case "started":
      return "info"
    case "in_progress":
      return "accent"
    case "finished":
      return "success"
    case "canceled":
    case "failed":
      return "alert"
    default:
      return "muted"
  }
}

function getStateBadgeVariation(state: OrderState | OrderPointState) {
  switch (state) {
    case "assigned":
    case "canceled":
    case "expired":
      return "accented"
    case "published":
    case "started":
    case "in_progress":
    case "finished":
    case "failed":
      return "solid"
    default:
      return "outlined"
  }
}
